import styled, { keyframes } from 'styled-components'
import { Loader2 } from 'lucide-react'
import { TYPOGRAPHY, SPACING, FONT_WEIGHT } from '../../../../styles/theme'

/**
 * Styled components for VentureNode component.
 * Venture nodes display project context as the parent of unmet-need nodes.
 */

// Field container for venture sections - transparent to let gradient show
export const FieldContainer = styled.div<{ $padding?: string }>`
  padding: ${(props) => props.$padding ?? '8px 12px'};
  color: var(--color-text-primary);
  display: flex;
  flex-direction: column;
  cursor: text;
  background-color: transparent;
`

// Field label (Name, Vision, etc.) - neutral grey for hierarchy
export const FieldLabel = styled.div<{ $color: string }>`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-tertiary);
  margin-bottom: 2px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

// Editable venture name input
export const VentureNameInput = styled.input`
  background: transparent;
  border: none;
  color: var(--color-text-primary);
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: normal;
  text-align: left;
  outline: none;
  width: 100%;
  font-family: inherit;
  line-height: 1.4;
  padding: ${SPACING.xs} 0;
  margin: 0;

  &::placeholder {
    color: var(--color-text-muted);
  }
`

// Editable textarea for venture fields (vision, strengths, goal, boundaries)
export const VentureFieldTextArea = styled.textarea`
  background: transparent;
  border: none;
  color: var(--color-text-primary);
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: normal;
  text-align: left;
  outline: none;
  resize: none;
  overflow: hidden;
  width: 100%;
  font-family: inherit;
  line-height: 1.4;
  padding: ${SPACING.xs} 0;
  margin: 0;
  word-wrap: break-word;
  white-space: pre-wrap;
  min-height: 40px;

  &::placeholder {
    color: var(--color-text-muted);
  }
`

// Section divider - subtle neutral line
export const SectionDivider = styled.div<{ $borderColor: string }>`
  border-top: 1px solid var(--color-node-header-border);
`

// ─── Document upload area styles ─────────────────────────────────────────────

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

export const SpinningLoader = styled(Loader2)`
  animation: ${spin} 1s linear infinite;
  flex-shrink: 0;
  opacity: 0.6;
`

export const DocumentUploadArea = styled.div`
  border: 1px dashed var(--color-node-header-border);
  border-radius: 6px;
  padding: 12px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s;
  background: rgba(0, 0, 0, 0.06);

  &:hover {
    border-color: var(--color-text-tertiary);
    background: rgba(0, 0, 0, 0.08);
  }
`

export const DocumentUploadText = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-muted);
  line-height: 1.4;
`

export const DocumentListItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  margin-bottom: 4px;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-secondary);
  line-height: 1.3;
  border: 1px solid rgba(59, 130, 246, 0.25);
  border-radius: 16px;
  background: rgba(59, 130, 246, 0.06);
`

export const DocumentFileName = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`

export const DocumentDeleteButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  color: var(--color-text-muted);
  display: flex;
  align-items: center;
  opacity: 0.6;
  transition: opacity 0.15s;

  &:hover {
    opacity: 1;
    color: var(--color-text-primary);
  }
`
